import {
  DestroyRef,
  Injectable,
  NgZone,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfigStateService } from '@abp/ng.core';
import { EMPTY, Observable, Subject, Subscription, firstValueFrom, fromEvent, interval, of, timer } from 'rxjs';
import { catchError, filter, finalize, switchMap } from 'rxjs/operators';
import { ScheduledCallAttemptOutcome } from 'src/app/features/conversation/models/scheduled-call-attempt-outcome.enum';
import { ScheduledCallAttemptState } from 'src/app/features/conversation/models/scheduled-call-attempt-state.enum';
import { CampaignOutboundPhase } from 'src/app/shared/agent-desktop/campaign/models/campaign-outbound-phase.enum';
import { CampaignOutboundService } from 'src/app/shared/agent-desktop/campaign/services/campaign-outbound.service';
import { CallStatus } from 'src/app/shared/agent-desktop/telephony/models/call-status.enum';
import { CallFacadeService } from 'src/app/shared/agent-desktop/telephony/services/call-facade.service';
import { isSelectableDuringScheduledCallOfferBcw } from 'src/core/helpers/agent-status.helper';
import { resolveCurrentUserKey } from 'src/core/helpers/current-user-key.helper';
import { RANDOM_FRACTION, randomDelayMs } from 'src/core/helpers/jitter.helper';
import { safeJsonParse } from 'src/core/helpers/json-parse.helper';
import { AgentStatusService } from 'src/core/services/agent-status/agent-status.service';
import { AgentHeartbeatService } from 'src/core/services/signalr/agent-heartbeat.service';
import { ZoneOutsideTicker } from '../../shared/zone-outside-ticker';
import { ScheduledCallBeginCallDto } from '../models/scheduled-call-begin-call.dto';
import { ScheduledCallLastClaimStamp } from '../models/scheduled-call-last-claim-stamp.model';
import { ScheduledCallOfferDto } from '../models/scheduled-call-offer.dto';
import { ScheduledCallOfferEndOutcome } from '../models/scheduled-call-offer-end-outcome.enum';
import { ScheduledCallOfferEndedEvent } from '../models/scheduled-call-offer-ended-event.model';
import { ScheduledCallOfferEndedReason } from '../models/scheduled-call-offer-ended-reason.enum';
import { ScheduledCallOfferPhase } from '../models/scheduled-call-offer-phase.enum';
import { ScheduledCallOfferTabMessage } from '../models/scheduled-call-offer-tab-message.model';
import { ScheduledCallAgentClientService } from './scheduled-call-agent-client.service';

const OFFER_TICK_INTERVAL_MS = 1000;
/** K-264: the server's due signal drives claims; this timed claim is only the safety net if a signal is lost. */
const CLAIM_INTERVAL_MS = 60000;
const CLAIM_BACKOFF_FACTOR = 2;
const CLAIM_MAX_INTERVAL_MS = 120000;
/** Another tab of this user claimed within this window: this tab skips its claim without a request. */
const CLAIM_DEDUPE_WINDOW_MS = 8000;
/** Every tab of the agent gets the same due signal; a random wait lets the first tab's stamp skip the others. */
const SIGNAL_CLAIM_SPREAD_MS = 750;
/** After a hub reconnect the read waits up to this long, so reconnecting tabs do not read in the same instant. */
const RECONNECT_READ_SPREAD_MS = 2000;
const STARTED_ELSEWHERE_REFRESH_INTERVAL_MS = 10000;
/** K-241: re-read delays after a failed read on leaving the offer BCW; then wait for the next trigger or 00:00. */
const BCW_EXIT_RETRY_DELAYS_MS: readonly number[] = [2000, 3000, 5000];
const MILLISECONDS_PER_SECOND = 1000;
const TAB_CHANNEL_NAME = 'agenticcx-scheduled-call-offer-sync';
const CALL_NOW_LOCK_PREFIX = 'agenticcx-scheduled-call-callnow-';
const LAST_CLAIM_STORAGE_PREFIX = 'agenticcx-scheduled-call-last-claim-';
const OFFER_TAKEN_MESSAGE: ScheduledCallOfferTabMessage['type'] = 'offer-taken';
const DIAL_FAILURE_OUTCOME = ScheduledCallAttemptOutcome.OriginateFailed;
const CAMPAIGN_ENGAGED_PHASES: readonly CampaignOutboundPhase[] = [
  CampaignOutboundPhase.Reserving,
  CampaignOutboundPhase.Bcw,
  CampaignOutboundPhase.Dialing,
];
const OFFER_BCW_PHASES: readonly ScheduledCallOfferPhase[] = [
  ScheduledCallOfferPhase.Offered,
  ScheduledCallOfferPhase.Starting,
];
const SETTLED_CALL_STATUSES: readonly CallStatus[] = [
  CallStatus.Terminated,
  CallStatus.Failed,
  CallStatus.Registered,
  CallStatus.Idle,
];

/** Agent Desktop callback offer: learning, countdown, Call Now, Abandon and Take (K-162, K-165, K-166, K-186). */
@Injectable({ providedIn: 'root' })
export class ScheduledCallOfferService {
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private readonly client = inject(ScheduledCallAgentClientService);
  private readonly facade = inject(CallFacadeService);
  private readonly agentStatus = inject(AgentStatusService);
  private readonly heartbeat = inject(AgentHeartbeatService);
  private readonly campaign = inject(CampaignOutboundService);
  private readonly configState = inject(ConfigStateService);
  private readonly randomFraction = inject(RANDOM_FRACTION);

  private readonly offerTicker = new ZoneOutsideTicker(this.zone, {
    intervalMs: OFFER_TICK_INTERVAL_MS,
    onTick: () => this.onOfferTick(),
  });
  private readonly refreshRequests = new Subject<void>();
  private readonly actionSettledSource = new Subject<void>();
  private readonly tabId = createTabId();
  private channel: BroadcastChannel | null = null;
  private claimLoop: Subscription | null = null;
  private signalClaim: Subscription | null = null;
  private claimInFlight = false;
  private claimFailures = 0;
  private refreshesPending = 0;
  private startedElsewhereRefresh: Subscription | null = null;
  private offerDeadline: number | null = null;
  private dialingAttemptId: number | null = null;
  /** Last attempt whose Call Now succeeded here or in another tab; never offered again in this tab. */
  private startedAttemptId: number | null = null;
  /** Last attempt this tab's own begin-call started; a later InProgress read of it is not a started-elsewhere card. */
  private ownStartedAttemptId: number | null = null;
  /** Last attempt the server pushed as Ended; a read answered before that commit must not bring it back. */
  private endedAttemptId: number | null = null;
  private sawEstablishedCall = false;
  private wasInitialized = false;
  private wasBcw = false;
  private bcwExitReadPending = false;
  private bcwExitRetries = 0;
  private bcwExitRetry: Subscription | null = null;
  /** HC-05: advances when the agent session ends; answers to requests sent in an older session are void. */
  private sessionGeneration = 0;
  /** RC3-R1: Call Now lock kept after a begin-call failure whose server outcome is unknown. */
  private keptCallNowLock: { attemptId: number; release: () => void } | null = null;

  private readonly offerState = signal<ScheduledCallOfferDto | null>(null);
  private readonly phaseState = signal(ScheduledCallOfferPhase.Idle);
  private readonly remainingSecondsState = signal(0);
  private readonly takenAttemptIdState = signal<number | null>(null);
  private readonly dialingContactIdState = signal<number | null>(null);
  private readonly verifyingStatusState = signal(false);

  readonly offer = this.offerState.asReadonly();
  readonly phase = this.phaseState.asReadonly();
  readonly remainingSeconds = this.remainingSecondsState.asReadonly();
  /** Contact of the dialed callback, kept through the call and its ACW (caller info, PREVIOUS CALLS). */
  readonly dialingContactId = this.dialingContactIdState.asReadonly();
  /** K-241: the agent left the offer BCW but its re-read failed; the card stays, silent and without Call Now. */
  readonly isVerifyingStatus = this.verifyingStatusState.asReadonly();
  /** One of this tab's own offer actions reached the server (Call Now result, Abandon, Timeout, Release). */
  readonly actionSettled$: Observable<void> = this.actionSettledSource.asObservable();

  /** Take was pressed for the current offer in this or another tab of this browser (D8 silences toast/sound). */
  readonly isTaken = computed(() => {
    const offer = this.offerState();
    return offer !== null && offer.attemptId === this.takenAttemptIdState();
  });

  readonly canCallNow = computed(
    () =>
      this.phaseState() === ScheduledCallOfferPhase.Offered &&
      this.facade.isRegistered() &&
      !this.verifyingStatusState(),
  );

  private readonly isTelephonyIdle = computed(
    () =>
      this.facade.currentSession() === null &&
      !this.facade.isCallActive() &&
      !this.facade.isAfterCallWork(),
  );

  /** Route-wide toast (154880/7, K-172): an offered callback while the phone is idle and Take was not pressed. */
  readonly toastVisible = computed(
    () =>
      this.phaseState() === ScheduledCallOfferPhase.Offered &&
      this.isTelephonyIdle() &&
      !this.isTaken() &&
      !this.verifyingStatusState(),
  );

  private readonly isCampaignEngaged = computed(
    () =>
      this.campaign.reservation() !== null ||
      CAMPAIGN_ENGAGED_PHASES.includes(this.campaign.phase()),
  );

  private readonly canClaim = computed(
    () =>
      this.agentStatus.isAvailable() &&
      this.isTelephonyIdle() &&
      this.facade.isRegistered() &&
      this.phaseState() === ScheduledCallOfferPhase.Idle &&
      !this.isCampaignEngaged(),
  );

  constructor() {
    this.refreshRequests
      .pipe(
        switchMap(() => {
          const generation = this.sessionGeneration;
          return this.client.getMyOffer().pipe(
            catchError(() => {
              if (this.isCurrentSession(generation)) {
                this.onRefreshFailed();
              }
              return EMPTY;
            }),
            filter(() => this.isCurrentSession(generation)),
            finalize(() => this.refreshesPending--),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(offer => this.onOfferLearned(offer));

    this.heartbeat.hubReconnected$
      .pipe(
        switchMap(() => timer(randomDelayMs(this.randomFraction, RECONNECT_READ_SPREAD_MS))),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.refreshOffer());

    this.heartbeat.scheduledCallOfferEnded$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => this.onOfferEnded(event));

    this.heartbeat.scheduledCallDue$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.onScheduledCallDue());

    fromEvent(document, 'visibilitychange')
      .pipe(
        filter(() => document.visibilityState === 'visible'),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.refreshOffer());

    effect(() => {
      const initialized = this.agentStatus.isInitialized();
      const isBcw = this.agentStatus.isBcw();
      untracked(() => this.onAgentStateChanged(initialized, isBcw));
    });

    effect(() => {
      const canClaim = this.canClaim();
      untracked(() => {
        this.heartbeat.setScheduledCallReadiness(canClaim);
        if (canClaim) {
          this.startClaimLoop();
        } else {
          this.stopClaimLoop();
        }
      });
    });

    effect(() => this.reconcileDial());

    effect(() => {
      const holdsBcw = OFFER_BCW_PHASES.includes(this.phaseState()) && !this.isCampaignEngaged();
      untracked(() => this.agentStatus.setScheduledCallOfferBcw(holdsBcw));
    });

    this.openChannel();

    this.destroyRef.onDestroy(() => {
      this.releaseKeptCallNowLock();
      this.offerTicker.stop();
      this.stopClaimLoop();
      this.heartbeat.setScheduledCallReadiness(false);
      this.channel?.close();
      this.channel = null;
    });
  }

  /** Call Now (154880/5): the agent starts the call; the system never dials on its own. */
  async callNow(): Promise<void> {
    const offer = this.offerState();
    if (!offer || !this.canCallNow()) {
      return;
    }

    this.phaseState.set(ScheduledCallOfferPhase.Starting);
    const generation = this.sessionGeneration;

    const locks = typeof navigator.locks?.request === 'function' ? navigator.locks : null;
    if (!locks) {
      await this.beginAndDial(offer, generation);
      return;
    }

    // RC3-R1: this tab still holds the attempt lock since its own begin-call, so no other tab of this browser started it.
    const kept = this.keptCallNowLock;
    if (kept?.attemptId === offer.attemptId) {
      await this.startUnderLock(offer, generation, true);
      if (this.keptCallNowLock === kept && !this.isOfferedAttempt(offer.attemptId, generation)) {
        this.releaseKeptCallNowLock();
      }
      return;
    }

    try {
      await locks.request(
        `${CALL_NOW_LOCK_PREFIX}${offer.attemptId}`,
        { ifAvailable: true },
        async lock => {
          if (!this.isCurrentStart(offer.attemptId, generation)) {
            return;
          }

          if (!lock) {
            this.onCallNowLockBusy();
            return;
          }

          if (await this.startUnderLock(offer, generation, false)) {
            await this.keepCallNowLock(offer.attemptId, generation);
          }
        },
      );
    } catch {
      if (this.isCurrentStart(offer.attemptId, generation)) {
        this.onCallNowLockBusy();
      }
    }
  }

  /** Returns true when begin-call failed without an answer: the server may still commit it, so the lock must be kept. */
  private async startUnderLock(
    offer: ScheduledCallOfferDto,
    generation: number,
    ownsUnknownBegin: boolean,
  ): Promise<boolean> {
    // The previous tab may have handed this attempt to SIP and released the lock before its Started push arrives.
    let currentOffer: ScheduledCallOfferDto | null;
    try {
      currentOffer = await firstValueFrom(this.client.getMyOffer());
    } catch {
      if (this.isCurrentStart(offer.attemptId, generation)) {
        this.onBeginCallRejected();
      }
      return false;
    }

    if (!this.isCurrentStart(offer.attemptId, generation)) {
      return false;
    }

    const sameAttempt = currentOffer?.attemptId === offer.attemptId;
    if (
      sameAttempt &&
      (currentOffer?.state === ScheduledCallAttemptState.Queued ||
        (ownsUnknownBegin && currentOffer?.state === ScheduledCallAttemptState.InProgress))
    ) {
      return this.beginAndDial(offer, generation);
    }

    if (sameAttempt && currentOffer?.state === ScheduledCallAttemptState.InProgress) {
      this.showStartedElsewhere(currentOffer);
      return false;
    }

    this.clearOffer();
    this.onOfferLearned(currentOffer);
    return false;
  }

  /** Holds the attempt lock until this tab's retry hands the call to SIP, the card closes or the service is destroyed. */
  private keepCallNowLock(attemptId: number, generation: number): Promise<void> {
    this.releaseKeptCallNowLock();
    if (!this.isOfferedAttempt(attemptId, generation)) {
      return Promise.resolve();
    }

    return new Promise<void>(release => (this.keptCallNowLock = { attemptId, release }));
  }

  private isOfferedAttempt(attemptId: number, generation: number): boolean {
    return (
      this.isCurrentSession(generation) &&
      this.phaseState() === ScheduledCallOfferPhase.Offered &&
      this.offerState()?.attemptId === attemptId
    );
  }

  private releaseKeptCallNowLock(): void {
    const kept = this.keptCallNowLock;
    this.keptCallNowLock = null;
    kept?.release();
  }

  private isCurrentStart(attemptId: number, generation: number): boolean {
    return (
      this.isCurrentSession(generation) &&
      this.phaseState() === ScheduledCallOfferPhase.Starting &&
      this.offerState()?.attemptId === attemptId
    );
  }

  /**
   * Runs under the per-attempt tab lock (when available) until the dial has been handed to SIP.
   * Returns true when begin-call failed and the card was handed back for a retry.
   */
  private async beginAndDial(offer: ScheduledCallOfferDto, generation: number): Promise<boolean> {
    let dial: ScheduledCallBeginCallDto;
    try {
      dial = await firstValueFrom(this.client.beginCall(offer.attemptId));
    } catch {
      if (!this.isCurrentStart(offer.attemptId, generation)) {
        return false;
      }

      this.onBeginCallRejected();
      return true;
    }

    // HC-05: the server-side start of an ended session is settled by the TAP/sweep (K-222), never dialed here.
    if (!this.isCurrentSession(generation)) {
      return false;
    }

    this.startedAttemptId = dial.attemptId;
    this.ownStartedAttemptId = dial.attemptId;
    // F2-04: a Break/sign-out POST from the offer BCW menu may still be in flight; its result decides.
    await this.agentStatus.whenStatusChangesSettled();

    if (
      !this.isCurrentSession(generation) ||
      this.phaseState() !== ScheduledCallOfferPhase.Starting ||
      this.offerState()?.attemptId !== offer.attemptId
    ) {
      return false;
    }

    // H3-02: Break or sign-out was chosen from the offer BCW menu while begin-call was in flight.
    if (isSelectableDuringScheduledCallOfferBcw(this.agentStatus.currentStateType())) {
      this.clearOffer();
      this.notifyActionSettled();
      return false;
    }

    this.closeOffer();
    this.dialingAttemptId = dial.attemptId;
    this.dialingContactIdState.set(offer.contactId);
    this.phaseState.set(ScheduledCallOfferPhase.Dialing);
    await this.facade.makeCall(dial.phoneNumber, dial.sipHeaders);
    return false;
  }

  /** Abandon (154880/5.4): a single press, no reason, no confirmation, no toast. */
  abandon(): void {
    const offer = this.offerState();
    if (!offer || this.phaseState() !== ScheduledCallOfferPhase.Offered) {
      return;
    }

    this.clearOffer();
    this.client
      .abandon(offer.attemptId)
      .pipe(
        finalize(() => this.notifyActionSettled()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({ error: () => this.refreshOffer() });
  }

  /** Take (154880/8.1–8.2, K-165): local only; tells the other tabs of this browser to silence the offer. */
  take(): void {
    const offer = this.offerState();
    if (!offer || this.phaseState() !== ScheduledCallOfferPhase.Offered) {
      return;
    }

    this.takenAttemptIdState.set(offer.attemptId);
    this.postTaken(offer.attemptId);
  }

  private refreshOffer(): void {
    if (!this.agentStatus.isInitialized()) {
      return;
    }

    this.refreshesPending++;
    this.refreshRequests.next();
  }

  private onAgentStateChanged(initialized: boolean, isBcw: boolean): void {
    const connected = initialized && !this.wasInitialized;
    const enteredBcw = isBcw && !this.wasBcw;
    const leftBcw = !isBcw && this.wasBcw;
    const sessionEnded = !initialized && this.wasInitialized;
    this.wasInitialized = initialized;
    this.wasBcw = isBcw;

    // 154880/6.1: a dropped session (lost permission, user switch) cannot reach the server; close the open offer
    // here, a K-220 started-elsewhere card with its poll (B-1) and an own Starting not yet handed to SIP (HC-05).
    if (sessionEnded) {
      this.sessionGeneration++;
      if (OFFER_BCW_PHASES.includes(this.phaseState())) {
        this.clearOffer();
      }
      return;
    }

    const offerByStatusHook =
      enteredBcw &&
      this.phaseState() === ScheduledCallOfferPhase.Idle &&
      !this.isCampaignEngaged();
    // 154880/6.1, 155203/4.2: the BCW exit hook ends the offer server-side without an ended push.
    const offerEndedByStatusChange = leftBcw && this.phaseState() === ScheduledCallOfferPhase.Offered;

    if (offerEndedByStatusChange) {
      this.bcwExitReadPending = true;
    }

    if (connected || offerByStatusHook || offerEndedByStatusChange) {
      this.refreshOffer();
    }
  }

  private startClaimLoop(): void {
    if (this.claimLoop) {
      return;
    }

    this.claimFailures = 0;
    this.scheduleClaim(randomDelayMs(this.randomFraction, CLAIM_INTERVAL_MS));
  }

  /** The tick runs outside Angular; only a real claim enters the zone, so a skipped tick costs no change detection. */
  private scheduleClaim(delayMs: number): void {
    this.claimLoop?.unsubscribe();
    this.claimLoop = this.zone.runOutsideAngular(() =>
      timer(delayMs)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.onClaimTick()),
    );
  }

  /** A skipped tick keeps the base interval: backoff follows only this tab's own failed claims. */
  private onClaimTick(): void {
    if (!this.tryClaim()) {
      this.scheduleClaim(CLAIM_INTERVAL_MS);
    }
  }

  /** K-264: signals during the wait add nothing; the claim is dropped if the tab can no longer claim when it ends. */
  private onScheduledCallDue(): void {
    if (this.signalClaim || !this.canClaim()) {
      return;
    }

    this.signalClaim = this.zone.runOutsideAngular(() =>
      timer(randomDelayMs(this.randomFraction, SIGNAL_CLAIM_SPREAD_MS))
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.signalClaim = null;
          if (this.canClaim()) {
            this.tryClaim();
          }
        }),
    );
  }

  /** The timer's and the signal's single claim path: one request in flight and the tabs' shared turn. */
  private tryClaim(): boolean {
    if (this.claimInFlight || !this.takeClaimTurn()) {
      return false;
    }

    this.zone.run(() => this.claimOnce());
    return true;
  }

  /** Fixed interval while claims succeed; consecutive failures double it up to the cap. */
  private nextClaimDelayMs(): number {
    return Math.min(CLAIM_INTERVAL_MS * CLAIM_BACKOFF_FACTOR ** this.claimFailures, CLAIM_MAX_INTERVAL_MS);
  }

  private onClaimSettled(failed: boolean): void {
    this.claimFailures = failed ? this.claimFailures + 1 : 0;
    if (this.claimLoop) {
      this.scheduleClaim(this.nextClaimDelayMs());
    }
  }

  /** F-7: outlives `stopClaimLoop` (the offer's own BCW push stops it); aborting left a server-side 500. */
  private claimOnce(): void {
    // A timed claim made a waiting signal claim redundant: its own stamp would not stop a second request.
    this.signalClaim?.unsubscribe();
    this.signalClaim = null;
    this.claimInFlight = true;
    const generation = this.sessionGeneration;
    let failed = false;
    this.client
      .claimNext()
      .pipe(
        catchError(() => {
          failed = true;
          return of(null);
        }),
        finalize(() => {
          this.claimInFlight = false;
          this.onClaimSettled(failed);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(offer => {
        if (!offer || !this.isCurrentSession(generation)) {
          return;
        }

        // The loop stopped meanwhile (BCW push, Break, sign-out): the server's current answer decides,
        // and a read already on its way is that answer — a second one would abort it.
        if (this.claimLoop) {
          this.onOfferLearned(offer);
        } else if (this.refreshesPending === 0) {
          this.refreshOffer();
        }
      });
  }

  /**
   * Each tab keeps its own loop; a tick right after another tab of this user claimed is skipped, so throttled
   * hidden tabs rarely add a request. Storage that cannot be read or written never blocks a claim.
   */
  private takeClaimTurn(): boolean {
    const userKey = this.currentUserKey();
    if (!userKey) {
      return true;
    }

    const storageKey = `${LAST_CLAIM_STORAGE_PREFIX}${userKey}`;
    const now = Date.now();
    try {
      const last = safeJsonParse<unknown>(localStorage.getItem(storageKey));
      const sinceLastMs = isLastClaimStamp(last) ? now - last.claimedAtMs : Number.POSITIVE_INFINITY;
      if (isLastClaimStamp(last) && last.tabId !== this.tabId && sinceLastMs >= 0 && sinceLastMs < CLAIM_DEDUPE_WINDOW_MS) {
        return false;
      }

      const stamp: ScheduledCallLastClaimStamp = { tabId: this.tabId, claimedAtMs: now };
      localStorage.setItem(storageKey, JSON.stringify(stamp));
    } catch {
      return true;
    }

    return true;
  }

  private stopClaimLoop(): void {
    this.claimLoop?.unsubscribe();
    this.claimLoop = null;
  }

  /** The reset sets `isInitialized` false before the effect advances the generation; both close the gap. */
  private isCurrentSession(generation: number): boolean {
    return generation === this.sessionGeneration && this.agentStatus.isInitialized();
  }

  private onOfferLearned(learned: ScheduledCallOfferDto | null): void {
    const phase = this.phaseState();
    if (phase === ScheduledCallOfferPhase.Dialing) {
      return;
    }

    this.stopBcwExitRetry();
    const offer = learned?.attemptId === this.endedAttemptId ? null : learned;
    if (!offer) {
      if (phase !== ScheduledCallOfferPhase.Idle) {
        this.clearOffer();
      }
      this.resyncHeldBcw();
      return;
    }

    const startedElsewhere = this.startedElsewhereRefresh !== null;
    // A Started push only voids a stale Queued read; another tab's InProgress attempt still gets the K-220 card here.
    const startedInAnotherTab =
      phase === ScheduledCallOfferPhase.Idle &&
      offer.state === ScheduledCallAttemptState.InProgress &&
      offer.attemptId !== this.ownStartedAttemptId;
    if (
      (phase === ScheduledCallOfferPhase.Starting && !startedElsewhere) ||
      (offer.attemptId === this.startedAttemptId && !startedInAnotherTab)
    ) {
      return;
    }

    if (offer.state === ScheduledCallAttemptState.InProgress) {
      this.showStartedElsewhere(offer);
      return;
    }

    if (!this.isTelephonyIdle()) {
      this.clearOffer();
      this.release(offer.attemptId);
      return;
    }

    this.presentOffer(offer);
  }

  /** H3-01 / F2-03: no offer holds this non-campaign BCW any more; the server's status decides, never a local guess. */
  private resyncHeldBcw(): void {
    if (this.agentStatus.isBcw() && !this.isCampaignEngaged()) {
      void this.agentStatus.refreshFromServer();
    }
  }

  private presentOffer(offer: ScheduledCallOfferDto): void {
    if (this.keptCallNowLock?.attemptId !== offer.attemptId) {
      this.releaseKeptCallNowLock();
    }

    this.stopStartedElsewhereRefresh();
    this.offerState.set(offer);
    this.offerDeadline = performance.now() + Math.max(0, offer.offerRemainingMs);
    this.phaseState.set(ScheduledCallOfferPhase.Offered);
    this.updateRemainingSeconds();
    this.offerTicker.start();
  }

  /** K-220: Call Now was pressed before F5 or in another tab; status only, no alarm, toast or countdown. */
  private showStartedElsewhere(offer: ScheduledCallOfferDto): void {
    if (this.startedElsewhereRefresh && this.offerState()?.attemptId === offer.attemptId) {
      return;
    }

    this.clearOffer();
    if (!this.isTelephonyIdle()) {
      return;
    }

    this.offerState.set(offer);
    this.phaseState.set(ScheduledCallOfferPhase.Starting);
    this.startedElsewhereRefresh = interval(STARTED_ELSEWHERE_REFRESH_INTERVAL_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshOffer());
  }

  private onOfferTick(): void {
    this.updateRemainingSeconds();
    if (this.remainingSecondsState() === 0 && this.phaseState() === ScheduledCallOfferPhase.Offered) {
      this.expireOffer();
    }
  }

  private updateRemainingSeconds(): void {
    const remainingMs = this.offerDeadline === null ? 0 : this.offerDeadline - performance.now();
    this.remainingSecondsState.set(Math.max(0, Math.ceil(remainingMs / MILLISECONDS_PER_SECOND)));
  }

  /**
   * 00:00 (154880/4.2): drop locally, then ask the server once; `NotExpired` re-arms from `getMyOffer`, `Started`
   * (a lost begin-call answer) re-reads it into the K-220 card and its poll.
   */
  private expireOffer(): void {
    const attemptId = this.offerState()?.attemptId;
    this.clearOffer();
    if (attemptId === undefined) {
      return;
    }

    this.client
      .timeout(attemptId)
      .pipe(
        finalize(() => this.notifyActionSettled()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: result => {
          if (
            result?.outcome === ScheduledCallOfferEndOutcome.NotExpired ||
            result?.outcome === ScheduledCallOfferEndOutcome.Started
          ) {
            this.refreshOffer();
          }
        },
        error: () => this.refreshOffer(),
      });
  }

  private onOfferEnded(event: ScheduledCallOfferEndedEvent): void {
    if (event.reason === ScheduledCallOfferEndedReason.Started) {
      this.startedAttemptId = event.attemptId;
    } else {
      this.endedAttemptId = event.attemptId;
    }

    if (this.offerState()?.attemptId !== event.attemptId) {
      return;
    }

    const phase = this.phaseState();
    const endsStarting =
      phase === ScheduledCallOfferPhase.Starting &&
      event.reason === ScheduledCallOfferEndedReason.Ended;

    // F2-03: started in another tab; poll like K-220, since a sweep of a lost carrier sends no push.
    const offer = this.offerState();
    if (phase === ScheduledCallOfferPhase.Offered && event.reason === ScheduledCallOfferEndedReason.Started && offer) {
      this.showStartedElsewhere({ ...offer, state: ScheduledCallAttemptState.InProgress });
      return;
    }

    if (phase === ScheduledCallOfferPhase.Offered || endsStarting) {
      this.clearOffer();
      this.resyncHeldBcw();
    }
  }

  /** Another tab of this browser is starting the same attempt; its `ended(Started)` push turns this card into Starting. */
  private onCallNowLockBusy(): void {
    if (this.phaseState() === ScheduledCallOfferPhase.Starting) {
      this.phaseState.set(ScheduledCallOfferPhase.Offered);
    }
  }

  private onBeginCallRejected(): void {
    if (this.phaseState() === ScheduledCallOfferPhase.Starting) {
      this.phaseState.set(ScheduledCallOfferPhase.Offered);
    }

    this.refreshOffer();
    this.notifyActionSettled();
  }

  /** Campaign `reconcileEngagement` pattern: an unanswered dial is reported once; an answered one ends after ACW. */
  private reconcileDial(): void {
    if (this.phaseState() !== ScheduledCallOfferPhase.Dialing) {
      this.sawEstablishedCall = false;
      return;
    }

    const status = this.facade.callStatus();
    const hasSession = this.facade.currentSession() !== null;
    if (this.facade.isAfterCallWork() || status === CallStatus.Active || status === CallStatus.Held) {
      this.sawEstablishedCall = true;
      return;
    }

    if (hasSession || !SETTLED_CALL_STATUSES.includes(status)) {
      return;
    }

    const attemptId = this.dialingAttemptId;
    const answered = this.sawEstablishedCall;
    this.dialingAttemptId = null;
    this.sawEstablishedCall = false;
    untracked(() => {
      this.dialingContactIdState.set(null);
      this.phaseState.set(ScheduledCallOfferPhase.Idle);
      if (!answered && attemptId !== null) {
        this.reportDialFailure(attemptId);
        return;
      }

      this.notifyActionSettled();
      // ACW Finish can land the agent in a new offer's BCW while still Dialing; that BCW entry was seen and skipped.
      if (this.wasBcw && this.agentStatus.isBcw() && !this.isCampaignEngaged()) {
        this.refreshOffer();
      }
    });
  }

  /** Frees the agent; once Call Now started the outcome is ignored and the TAP writes the carrier leg's real result (K-222). */
  private reportDialFailure(attemptId: number): void {
    this.client
      .reportCallFailed(attemptId, { outcome: DIAL_FAILURE_OUTCOME })
      .pipe(
        finalize(() => this.notifyActionSettled()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({ error: () => this.refreshOffer() });
  }

  /** KA-10d: an offer learned while a call rings, runs or is in ACW is handed back uncounted. */
  private release(attemptId: number): void {
    this.client
      .release(attemptId)
      .pipe(
        finalize(() => this.notifyActionSettled()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({ error: () => undefined });
  }

  private notifyActionSettled(): void {
    this.actionSettledSource.next();
  }

  /** K-241: a failed re-read after leaving the offer BCW is retried a few times; the card waits, never closed locally. */
  private onRefreshFailed(): void {
    if (!this.bcwExitReadPending || this.phaseState() !== ScheduledCallOfferPhase.Offered) {
      return;
    }

    this.verifyingStatusState.set(true);
    const delayMs = BCW_EXIT_RETRY_DELAYS_MS[this.bcwExitRetries];
    if (delayMs === undefined) {
      return;
    }

    this.bcwExitRetries++;
    this.bcwExitRetry?.unsubscribe();
    this.bcwExitRetry = timer(delayMs)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshOffer());
  }

  private stopBcwExitRetry(): void {
    this.bcwExitRetry?.unsubscribe();
    this.bcwExitRetry = null;
    this.bcwExitRetries = 0;
    this.bcwExitReadPending = false;
    this.verifyingStatusState.set(false);
  }

  private stopStartedElsewhereRefresh(): void {
    this.startedElsewhereRefresh?.unsubscribe();
    this.startedElsewhereRefresh = null;
  }

  private closeOffer(): void {
    this.stopStartedElsewhereRefresh();
    this.stopBcwExitRetry();
    this.offerTicker.stop();
    this.offerDeadline = null;
    this.offerState.set(null);
    this.remainingSecondsState.set(0);
  }

  private clearOffer(): void {
    this.releaseKeptCallNowLock();
    this.closeOffer();
    this.phaseState.set(ScheduledCallOfferPhase.Idle);
  }

  private openChannel(): void {
    if (typeof BroadcastChannel === 'undefined') {
      return;
    }

    this.channel = new BroadcastChannel(TAB_CHANNEL_NAME);
    this.channel.addEventListener('message', event => {
      this.zone.run(() => this.onTabMessage(event.data as ScheduledCallOfferTabMessage | null));
    });
  }

  private postTaken(attemptId: number): void {
    const userKey = this.currentUserKey();
    if (!this.channel || !userKey) {
      return;
    }

    const message: ScheduledCallOfferTabMessage = {
      type: OFFER_TAKEN_MESSAGE,
      tabId: this.tabId,
      userKey,
      attemptId,
    };
    this.channel.postMessage(message);
  }

  private onTabMessage(message: ScheduledCallOfferTabMessage | null): void {
    if (
      message?.type !== OFFER_TAKEN_MESSAGE ||
      message.tabId === this.tabId ||
      message.userKey !== this.currentUserKey()
    ) {
      return;
    }

    this.takenAttemptIdState.set(message.attemptId);
  }

  private currentUserKey(): string | null {
    return resolveCurrentUserKey(this.configState.getDeep('currentUser'));
  }
}

function isLastClaimStamp(value: unknown): value is ScheduledCallLastClaimStamp {
  const stamp = value as Partial<ScheduledCallLastClaimStamp> | null;
  return typeof stamp?.tabId === 'string' && typeof stamp.claimedAtMs === 'number';
}

function createTabId(): string {
  const cryptoRef = globalThis.crypto;
  if (typeof cryptoRef?.randomUUID === 'function') {
    return cryptoRef.randomUUID();
  }

  return `tab-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}
